import { CircleCheck as TabIconCircleCheck, Clock as TabIconClock } from "lucide-react";
import { getDeferredOutAmounts, ClientLateSettledTransaction } from "./clientEarningsData";
import { useLocale } from "../../../../hooks/useLocale";
import { msg } from "../../../../locales/translate.ts";
import { Select } from "@ui/select/Select";
import { saveAs } from "file-saver";
import { ChevronRight, CircleCheck, Clock, FileDown, LoaderCircle, RotateCw, TriangleAlert } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";
import { GD } from "src/GD";
import { getStatementTypeLabel } from "src/tools/statements";
import type { IPaymentSession } from "src/types/types";
import { AdaptiveTabBar } from "src/view/conponents/AdaptiveTabBar";
import { S_STATEMENT_POPUP } from "src/view/reports/StatementPopup";
import { formatProfitAmount } from "src/view/statView/accountantData";
import { AccountingCard as Card, ReserveRefreshButton } from "src/view/statView/styled";
import styled, { css } from "styled-components";
import { getClientEarningsRequest, parseClientEarnings, type ClientEarningsData, type ClientEarningsMetrics, type ClientUnsettledTransaction } from "./clientEarningsData";

// A calmer layout of the same report: the answer on top, the payout breakdown, details folded away.
// Every figure here is shown exactly as the server sends it: nothing is summed, counted, derived or defaulted in the browser.
// A figure the server did not send is shown as "—", never as zero.

const Head = styled.div`
	display: flex;
	align-items: flex-end;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: 12px 20px;
	.ctx { color: #667085; font-size: 11px; }
	h2 { margin: 2px 0 0; color: #182230; font-size: 21px; font-weight: 600; line-height: 1.3; }
	.period { margin-top: 2px; color: #344054; font-size: 15px; }
	.tools { display: flex; align-items: center; gap: 6px; }
`;

// The light area runs to the card edges under the tabs; the card's own padding is 20px.
const Body = styled.div`
	display: flex;
	flex-direction: column;
	gap: 20px;
	margin: 0 -20px -20px;
	padding: 20px;
	border-top: 1px solid #eaecf0;
	border-radius: 0 0 12px 12px;
	background: #f5f7fa;
	color: #344054;
	font-size: 14px;
	line-height: 1.5;
	.num { font-variant-numeric: tabular-nums; white-space: nowrap; }
	> [role="status"]:empty { display: none; }
`;

const Status = styled.div<{ $tone: "ok" | "warn" | "open" }>`
	display: flex;
	align-items: flex-start;
	gap: 10px;
	padding: 12px 16px;
	border: 1px solid ${p => p.$tone === "warn" ? "#f5d9a8" : "#d0d5dd"};
	border-radius: 10px;
	background: ${p => p.$tone === "warn" ? "#fff6e6" : "#fff"};
	svg { flex-shrink: 0; margin-top: 2px; color: ${p => p.$tone === "ok" ? "#157347" : p.$tone === "warn" ? "#93570a" : "#667085"}; }
	b { color: ${p => p.$tone === "warn" ? "#93570a" : "#182230"}; font-weight: 600; }
	.extra { display: block; margin-top: 2px; color: #667085; font-size: 13px; }
`;

const Tiles = styled.dl<{ $count: number }>`
	display: grid;
	grid-template-columns: repeat(${p => p.$count}, minmax(0, 1fr));
	margin: 0;
	border: 1px solid #d0d5dd;
	border-radius: 12px;
	background: #fff;
	box-shadow: 0 1px 2px rgba(16, 24, 40, .05);
	> div { display: flex; flex-direction: column; gap: 4px; min-width: 0; padding: 18px 20px; }
	> div + div { border-left: 1px solid #eaecf0; }
	dt { color: #667085; font-size: 13px; }
	dd { margin: 0; color: #182230; font-size: 24px; font-weight: 600; line-height: 1.3; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
	.hint { color: #98a2b3; font-size: 12px; }
	@media (max-width: 760px) {
		grid-template-columns: minmax(0, 1fr);
		> div + div { border-left: 0; border-top: 1px solid #eaecf0; }
	}
`;

const Section = styled.section`
	display: flex;
	flex-direction: column;
	gap: 8px;
	min-width: 0;
	> h3 { margin: 0; padding-left: 2px; color: #667085; font-size: 11px; font-weight: 600; letter-spacing: .04em; text-transform: uppercase; }
	.aside { margin: 0; padding: 0 2px; color: #667085; font-size: 13px; }
	.aside b { color: #344054; font-weight: 600; }
`;

const surface = css`
	min-width: 0;
	border: 1px solid #d0d5dd;
	border-radius: 12px;
	background: #fff;
	box-shadow: 0 1px 2px rgba(16, 24, 40, .05);
	.chev { flex-shrink: 0; color: #98a2b3; transition: transform .15s; }
	details[open] > summary .chev { transform: rotate(90deg); }
	summary { list-style: none; cursor: pointer; }
	summary::-webkit-details-marker { display: none; }
	summary:focus-visible, button:focus-visible { outline: 2px solid #6374d9; outline-offset: 2px; }
	@media (prefers-reduced-motion: reduce) { .chev { transition: none; } }
`;

const Box = styled.div`${surface}`;

const Calc = styled.ul`
	margin: 0;
	padding: 0;
	list-style: none;
	> li { display: flex; align-items: baseline; justify-content: space-between; gap: 16px; padding: 11px 20px; }
	> li + li { border-top: 1px solid #eaecf0; }
	.label { flex: 1 1 auto; min-width: 0; }
	.amount { flex: 0 0 auto; color: #182230; font-variant-numeric: tabular-nums; white-space: nowrap; }
	.amount.muted { color: #98a2b3; }
	.total { border-top-color: #d0d5dd; color: #182230; font-size: 15px; font-weight: 600; }
	.total .amount { font-size: 18px; }
`;

const BoxNote = styled.p`
	margin: 0;
	padding: 10px 20px 12px;
	border-top: 1px solid #eaecf0;
	color: #98a2b3;
	font-size: 12px;
`;

const Rows = styled.ul`
	margin: 0;
	padding: 0;
	list-style: none;
	> li + li { border-top: 1px solid #eaecf0; }
	.row { display: grid; grid-template-columns: minmax(0, 1fr) auto; align-items: baseline; gap: 4px 16px; padding: 14px 20px; }
	summary.row:hover { background: #f8fafc; }
	.what { display: flex; align-items: center; gap: 6px; color: #182230; font-weight: 500; }
	.what.plain { padding-left: 22px; }
	.why { grid-column: 1 / -1; padding-left: 22px; color: #667085; font-size: 13px; }
	.why b { color: #344054; font-weight: 600; }
	.amount { color: #182230; font-size: 17px; font-weight: 600; font-variant-numeric: tabular-nums; white-space: nowrap; }
	.amount.muted { color: #98a2b3; font-weight: 500; }
	.badge { margin-left: 4px; padding: 1px 8px; border: 1px solid #f5d9a8; border-radius: 6px; background: #fff6e6; color: #93570a; font-size: 12px; font-weight: 500; white-space: nowrap; }
	.total { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 16px; padding: 14px 20px; border-top: 1px solid #eaecf0; border-radius: 0 0 12px 12px; background: #f8fafc; color: #182230; font-weight: 600; }
	.total .amount { font-size: 18px; }
`;

const Entries = styled.ul`
	display: flex;
	flex-direction: column;
	max-height: 320px;
	margin: 0;
	padding: 0 20px 12px 42px;
	overflow-y: auto;
	list-style: none;
	li { border-top: 1px dashed #eaecf0; }
	/* Fixed columns so ids, dates and amounts line up from row to row whatever the id length. */
	button { display: grid; grid-template-columns: 17em minmax(0, 1fr) auto; align-items: baseline; gap: 2px 16px; width: 100%; padding: 7px 0; border: 0; background: transparent; color: #344054; font: inherit; font-size: 13px; text-align: left; cursor: pointer; }
	.id { color: #25638e; font-weight: 500; font-variant-numeric: tabular-nums; white-space: nowrap; }
	button:hover .id { text-decoration: underline; text-underline-offset: 3px; }
	.meta { min-width: 0; color: #667085; }
	.num { text-align: right; }
	@media (max-width: 640px) {
		button { grid-template-columns: minmax(0, 1fr) auto; }
		.meta { grid-column: 1 / -1; grid-row: 2; }
	}
	.more { padding: 7px 0; color: #667085; font-size: 12px; }
`;

const Fold = styled.details`
	${surface}
	> summary { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 4px 12px; padding: 14px 20px; border-radius: 12px; }
	> summary:hover { background: #f8fafc; }
	&[open] > summary { border-radius: 12px 12px 0 0; }
	.fold-title { display: flex; align-items: center; gap: 6px; color: #182230; font-size: 15px; font-weight: 600; }
	.fold-meta { color: #667085; font-size: 13px; }
	.fold-body { display: flex; flex-direction: column; gap: 12px; padding: 14px 20px 18px; border-top: 1px solid #eaecf0; }
	.plain { margin: 0; max-width: 72ch; color: #667085; font-size: 13px; }
	.formula { font-size: 13px; line-height: 1.9; font-variant-numeric: tabular-nums; }
	.formula em { color: #667085; font-style: normal; }
	.formula b { color: #182230; font-weight: 600; }
	.notes { margin: 0; padding-left: 18px; color: #667085; font-size: 13px; }
	.notes-title { color: #667085; font-size: 11px; font-weight: 600; letter-spacing: .04em; text-transform: uppercase; }
`;

const Table = styled.div`
	max-width: 100%;
	overflow-x: auto;
	table { width: 100%; min-width: 680px; border-collapse: collapse; font-size: 13px; }
	th, td { padding: 9px 10px; border-bottom: 1px solid #eaecf0; text-align: right; white-space: nowrap; }
	th { color: #667085; font-size: 12px; font-weight: 500; }
	th:first-child, td:first-child { text-align: left; }
	td { color: #344054; font-variant-numeric: tabular-nums; }
	td.zero { color: #98a2b3; }
	tr.sum td { border-bottom: 0; color: #182230; font-weight: 600; }
	tbody tr[data-clickable] { cursor: pointer; }
	tbody tr[data-clickable]:hover { background: #f8fafc; }
	.link { padding: 0; border: 0; background: transparent; color: #25638e; font: inherit; font-weight: 500; cursor: pointer; }
	.link:hover { text-decoration: underline; text-underline-offset: 3px; }
	.chip { display: inline-block; padding: 2px 8px; border: 1px solid #d0d5dd; border-radius: 6px; color: #667085; font-size: 12px; }
	.chip.full { border-color: #b7dfc6; color: #157347; }
	.chip.partial { border-color: #f5d9a8; color: #93570a; }
`;

// Server timestamps without an offset are UTC.
const parseServerDate = (value: string) => {
	const iso = value.trim().replace(" ", "T");
	const date = new Date(iso.includes("T") && !/(Z|[+-]\d{2}:?\d{2})$/i.test(iso) ? `${iso}Z` : iso);
	return Number.isNaN(date.getTime()) ? undefined : date;
};

const isZero = (amount: number) => Math.round(amount * 100) === 0;

interface ClientEarningsCalmCardProps {
	clientId: number;
	clientName?: string;
	projectId: number;
	userId: number;
	from: string;
	to: string;
}

export const ClientEarningsCalmCard = ({ clientId, clientName, projectId, userId, from, to }: ClientEarningsCalmCardProps) => {
	useLocale();
	const { t, i18n } = useTranslation();
	const [view, setView] = useState<"accrued" | "paid">("paid");
	const [selectedCurrency, setSelectedCurrency] = useState("EUR");
	const [attempt, setAttempt] = useState(0);
	const [state, setState] = useState<{ busy: boolean; data?: ClientEarningsData; error?: string }>({ busy: true });
	const [pdfBusy, setPdfBusy] = useState(false);
	const [pdfError, setPdfError] = useState(false);
	const exportVersion = useRef(0);

	useEffect(() => {
		let cancelled = false;
		exportVersion.current += 1;
		setPdfBusy(false);
		setPdfError(false);
		const subscription = GD.S_AUTH_LOG_OUT.subscribe(() => {
			cancelled = true;
			exportVersion.current += 1;
			setPdfBusy(false);
			setPdfError(false);
			setState({ busy: false });
		});
		setState({ busy: true });
		const load = async () => {
			const checkContext = async () => {
				const [profile, project] = await Promise.all([GD.REQ_PROFILE.request(), GD.REQ_CURRENT_PROJECT.request()]);
				if (cancelled || profile?.id !== userId || project?.id !== projectId) throw new Error("Context changed");
			};
			try {
				await checkContext();
				const response = await GD.REQ_API.request({ ...getClientEarningsRequest(clientId, from, to), expectedProjectId: projectId });
				await checkContext();
				if (response?.error || response?.data === undefined || response.data === null) throw new Error(msg("earningsRequestFailed"));
				const data = parseClientEarnings(response.data, clientId, from, to);
				if (!data) throw new Error(msg("unexpectedEarningsResponse"));
				if (!cancelled) setState({ busy: false, data });
			} catch {
				if (!cancelled) setState({ busy: false, error: t("accountant.earningsError") });
			}
		};
		void load();
		return () => { cancelled = true; exportVersion.current += 1; GD.S_AUTH_LOG_OUT.unsubscribe(subscription); };
	}, [clientId, projectId, userId, from, to, attempt, t]);

	const downloadPdf = async () => {
		const data = state.data;
		if (!data || state.busy || pdfBusy || data.clientId !== clientId || data.period.from !== from || data.period.to !== to) return;
		const version = exportVersion.current;
		const translate = i18n.getFixedT(i18n.resolvedLanguage ?? i18n.language);
		setPdfBusy(true);
		setPdfError(false);
		try {
			const { renderClientEarningsPDF } = await import("src/tools/pdf/ClientEarningsPDF");
			const blob = await renderClientEarningsPDF({ data, clientName, t: translate });
			if (version !== exportVersion.current) return;
			const [profile, project] = await Promise.all([GD.REQ_PROFILE.request(), GD.REQ_CURRENT_PROJECT.request()]);
			if (version !== exportVersion.current || profile?.id !== userId || project?.id !== projectId) return;
			saveAs(blob, `earnings_${data.clientId}_${data.period.from}_${data.period.to}.pdf`);
		} catch {
			if (version === exportVersion.current) setPdfError(true);
		} finally {
			if (version === exportVersion.current) setPdfBusy(false);
		}
	};

	const locale = t("accountant.locale");
	const c = (key: string, options?: Record<string, unknown>) => t(`accountant.calm.${key}`, options);
	const data = state.data;
	const section = data?.[view];
	const currencies = section ? Object.keys(section.byCurrency) : [];
	const currency = currencies.includes(selectedCurrency) ? selectedCurrency : currencies[0] ?? "EUR";
	const money = (amount: number) => formatProfitAmount(amount, currency, locale);
	const count = (value: number) => new Intl.NumberFormat(locale).format(value);
	const day = (value: string, withYear = false) => new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", ...(withYear ? { year: "numeric" } : {}), timeZone: "UTC" }).format(new Date(`${value}T00:00:00Z`));
	const dayRange = (start: string, end: string, withYear = false) => start === end ? day(start, withYear) : `${day(start)} – ${day(end, withYear)}`;
	const metrics = section?.byCurrency[currency];

	// The period is open while the server bills it only up to a day before its end; the open days come from the server only.
	const paid = view === "paid";
	const closedThrough = paid ? data?.closedThrough : undefined;
	const serverTail = paid ? data?.openTail : undefined;
	const periodOpen = !!serverTail || (!!closedThrough && closedThrough < to);
	const tailFrom = serverTail?.from;
	const tailAmount = serverTail?.byCurrency[currency];
	const deferred = paid ? data?.paid.deferred : undefined;
	const notPaidOut = paid ? data?.paid.notPaidOut : undefined;
	const unsettled = paid ? data?.unsettled : undefined;
	const byPeriod = paid ? data?.paid.byPeriod : undefined;
	const overview = paid ? data?.turnoverInRange?.[currency] : undefined;
	const deferredOut = paid && data ? getDeferredOutAmounts(data.paid)[currency] : undefined;
	const lateSettled = paid ? data?.paid.lateSettled : undefined;
	const billedTransactions = lateSettled?.transactions.filter(transaction => transaction.billedBy !== null && transaction.currency === currency) ?? [];
	const notBilledTransactions = lateSettled?.transactions.filter(transaction => transaction.billedBy === null && transaction.currency === currency) ?? [];
	const notBilledAmount = notPaidOut?.notBilled?.[currency];
	const showNotBilled = !!notPaidOut?.notBilled || !!notBilledTransactions.length;
	const hasNotBilled = (notBilledAmount !== undefined && !isZero(notBilledAmount)) || !!notBilledTransactions.length;
	const deferredIn = deferred?.in[currency];
	const deferredBeyond = deferred?.beyond[currency];
	const unsettledAmount = notPaidOut?.unsettled[currency] ?? unsettled?.byCurrency[currency]?.amount;
	const notPaidOutTotal = notPaidOut?.total[currency];
	const unexplained = paid ? data?.reconciliation.turnoverUnexplained?.[currency] : undefined;
	const reportsUnexplained = paid ? data?.reconciliation.reportsUnexplained?.[currency] : undefined;
	const turnoverMismatch = unexplained !== undefined && !isZero(unexplained);
	const laterMismatch = reportsUnexplained !== undefined && !isZero(reportsUnexplained);
	const mismatch = !periodOpen && (turnoverMismatch || laterMismatch);
	const mismatchAmount = turnoverMismatch ? unexplained : reportsUnexplained;
	const reconciled = !periodOpen && unexplained !== undefined && !mismatch && !!metrics;
	const rounding = paid ? data?.reconciliation.documentsRounding?.[currency] : undefined;
	const shiftShown = paid && !!data?.reportedLater && !!data?.reportedEarlier;
	const reportedLater = shiftShown ? data?.reportedLater?.byCurrency[currency]?.amount : undefined;
	const reportedEarlier = shiftShown ? data?.reportedEarlier?.byCurrency[currency]?.amount : undefined;
	const insideTurnover = byPeriod?.inside[currency]?.turnover;
	const statements = data?.paid.statements.filter(statement => statement.net[currency] !== undefined) ?? [];
	const pendingAmount = typeof data?.paid.pending[currency] === "number" ? data.paid.pending[currency] as number : undefined;
	const channels = section?.channels.filter(channel => channel.currencies[currency]) ?? [];
	const channelTotal = paid ? byPeriod?.inside[currency] : undefined;

	// Stuck figures come only from the server's stuck group; payments are listed in the server's order and never sorted into groups here.
	const stuckAfter = unsettled?.stuckAfterDays;
	const stuckCount = unsettled?.stuck?.count;
	const stuckAmount = unsettled?.stuck?.byCurrency[currency]?.amount;
	const lostStatuses = unsettled ? Object.entries(unsettled.byStatus).filter(([status, byCode]) => (status === "failed" || status === "error") && byCode[currency] && !isZero(byCode[currency].amount)) : [];
	const transactions = unsettled?.transactions ?? [];

	const typeLabel = (type: string) => type ? c(`statementType.${type.trim().toLowerCase()}`, { defaultValue: getStatementTypeLabel(type) }) : "—";
	const statusLabel = (status: string) => {
		const fallback = status.replace(/[_-]+/g, " ").trim();
		const label = status ? c(`statementStatus.${status.trim().toLowerCase()}`, { defaultValue: fallback }) : "—";
		return `${label.charAt(0).toUpperCase()}${label.slice(1)}`;
	};
	const transactionMeta = (transaction: ClientUnsettledTransaction) => {
		const date = parseServerDate(transaction.createdAt);
		const when = date ? `${new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "UTC" }).format(date)} UTC` : transaction.createdAt;
		const status = transaction.status && t(`accountant.settlementStatus.${transaction.status}`, { defaultValue: transaction.status.replace(/[_-]+/g, " ") });
		const waiting = transaction.ageDays !== undefined ? c("notPaid.waiting", { count: transaction.ageDays }) : "";
		return [when, status, waiting].filter(Boolean).join(" · ");
	};
	const openTransaction = (transaction: ClientUnsettledTransaction) => {
		// Ids stay strings; the preview takes the numeric session id only while it is exact.
		const sessionId = /^\d+$/.test(transaction.id) && Number.isSafeInteger(Number(transaction.id)) ? Number(transaction.id) : transaction.id;
		GD.S_TRANSACTION_PREVIEW.invoke({ id: sessionId } as unknown as IPaymentSession);
	};
	const shown = (amount: number | undefined) => amount === undefined ? "—" : money(amount);
	const atLeast = (amount: number | undefined) => amount === undefined ? "—" : mismatch ? c("notPaid.atLeast", { amount: money(amount) }) : money(amount);
	const muted = (amount: number | undefined) => amount === undefined || isZero(amount) ? " muted" : "";

	const renderStatus = () => {
		if (!paid || !data) return null;
		if (periodOpen) return <Status $tone="open" role="status">
			<Clock size={18} aria-hidden="true" />
			<div>
				<Trans t={t} i18nKey={closedThrough ? tailFrom ? "accountant.calm.status.open" : "accountant.calm.status.openThrough" : "accountant.calm.status.openNone"} values={{ through: closedThrough ? day(closedThrough) : "", days: tailFrom ? dayRange(tailFrom, to) : "" }} components={{ b: <b /> }} />
				{tailAmount && <span className="extra">{c("status.openAmount", { amount: money(tailAmount.amount), count: tailAmount.count })}</span>}
			</div>
		</Status>;
		if (mismatch) return <Status $tone="warn" role="status"><TriangleAlert size={18} aria-hidden="true" /><div><Trans t={t} i18nKey="accountant.calm.status.mismatch" values={{ amount: shown(mismatchAmount) }} components={{ b: <b /> }} /></div></Status>;
		if (data.reconciliation.uncoveredDays > 0) return <Status $tone="warn" role="status"><TriangleAlert size={18} aria-hidden="true" /><div><Trans t={t} i18nKey="accountant.calm.status.uncovered" count={data.reconciliation.uncoveredDays} values={{ count: data.reconciliation.uncoveredDays }} components={{ b: <b /> }} /></div></Status>;
		if (reconciled) return <Status $tone="ok" role="status"><CircleCheck size={18} aria-hidden="true" /><div><Trans t={t} i18nKey="accountant.calm.status.ok" components={{ b: <b /> }} /></div></Status>;
		return <Status $tone="ok" role="status"><CircleCheck size={18} aria-hidden="true" /><div>{c("status.covered")}</div></Status>;
	};

	const renderTiles = () => {
		if (!data || !metrics) return null;
		const tiles: { key: string; label: string; value: string; hint: string }[] = [];
		if (paid && overview) tiles.push({ key: "overview", label: c("tiles.overview"), value: money(overview.amount), hint: c("tiles.overviewHint", { count: overview.count }) });
		tiles.push(paid
			? { key: "net", label: c("tiles.paidNet"), value: money(metrics.net), hint: c("tiles.paidNetHint") }
			: { key: "net", label: c("tiles.accruedNet"), value: money(metrics.net), hint: c("tiles.accruedHint") });
		if (paid && (deferred || unsettled || lateSettled || showNotBilled)) tiles.push({
			key: "notPaid", label: c("tiles.notPaid"),
			value: reconciled && notPaidOutTotal !== undefined ? money(notPaidOutTotal) : "—",
			hint: mismatch ? c("tiles.notPaidMismatch") : periodOpen ? c("tiles.notPaidOpen") : c("tiles.notPaidHint"),
		});
		if (!paid) tiles.push({ key: "days", label: c("tiles.days"), value: count(data.accrued.daysCovered), hint: c("tiles.daysHint") });
		return <Tiles $count={tiles.length}>{tiles.map(tile => <div key={tile.key}><dt>{tile.label}</dt><dd>{tile.value}</dd><span className="hint">{tile.hint}</span></div>)}</Tiles>;
	};

	const renderCalc = () => {
		if (!metrics) return <p className="aside">{c("calc.empty")}</p>;
		// Each line is a server field as sent, zeros included; OCT appears only when the server sends it.
		const lines: { key: string; label: string; value: number | undefined }[] = [
			{ key: "fees", label: c("calc.fees"), value: metrics.fees },
			{ key: "rollingTaken", label: c("calc.rollingTaken"), value: metrics.rollingTaken },
			{ key: "rollingReleased", label: c("calc.rollingReleased"), value: metrics.rollingReleased },
			{ key: "refunds", label: c("calc.refunds"), value: metrics.refunds },
			{ key: "chargebacks", label: c("calc.chargebacks"), value: metrics.chargebacks },
			...(metrics.oct !== undefined ? [{ key: "oct", label: c("calc.oct"), value: metrics.oct }] : []),
			...(paid ? [
				{ key: "withdrawal", label: c("calc.withdrawal"), value: metrics.withdrawal },
				{ key: "setup", label: c("calc.setup"), value: metrics.setup },
				{ key: "monthly", label: c("calc.monthly"), value: metrics.monthly },
			] : []),
		];
		return <Box>
			<Calc>
				<li><span className="label">{c(paid ? "calc.turnoverDocs" : "calc.turnover")}</span><span className="amount">{money(metrics.turnover)}</span></li>
				{lines.map(line => <li key={line.key}><span className="label">{line.label}</span><span className={`amount${muted(line.value)}`}>{shown(line.value)}</span></li>)}
				<li className="total"><span className="label">{c(paid ? "calc.netPaid" : "calc.netAccrued")}</span><span className="amount">{money(metrics.net)}</span></li>
			</Calc>
			{rounding !== undefined && !isZero(rounding) && <BoxNote>{c("calc.rounding", { amount: money(rounding) })}</BoxNote>}
		</Box>;
	};

	const renderLateSettledEntries = (transactions: ClientLateSettledTransaction[]) => <Entries>{transactions.map(transaction => <li key={transaction.id}>
		<button type="button" style={transaction.billedBy === null ? { color: "#b42318", background: "#fef3f2" } : undefined} aria-label={t("accountant.openTransaction", { id: transaction.id })} onClick={() => openTransaction(transaction)}>
			<span className="id" style={"billedBy" in transaction && transaction.billedBy === null ? { color: "#b42318" } : undefined}>{c("notPaid.payment", { id: transaction.id })}</span>
			<span className="meta" style={transaction.billedBy === null ? { color: "#b42318" } : undefined}>{transaction.billedBy ? `${t("accountant.statement")} #${transaction.billedBy.id}` : t("accountant.notBilled")}</span>
			<span className="num">{formatProfitAmount(transaction.amount, transaction.currency, locale)}</span>
		</button>
	</li>)}</Entries>;
	const renderNotPaid = () => {
		if (!paid || !data || !(deferred || unsettled || lateSettled || showNotBilled)) return null;
		const outStatements = deferred?.outStatements.filter(statement => statement.amount[currency] !== undefined) ?? [];
		const unsettledCount = unsettled?.byCurrency[currency]?.count;
		return <Section aria-label={c("notPaid.title")}>
			<h3>{c("notPaid.title")}</h3>
			<Box>
				<Rows>
					{(deferred || lateSettled) && <li>{outStatements.length || billedTransactions.length ? <details>
						<summary className="row">
							<span className="what"><ChevronRight size={16} className="chev" aria-hidden="true" />{c("notPaid.next")}</span>
							<span className={`amount${muted(deferredOut)}`}>{atLeast(deferredOut)}</span>
							<span className="why">{c("notPaid.nextHint")} {!lateSettled && c("notPaid.payments", { count: deferred?.outCount ?? 0 })}</span>
						</summary>
						<Entries>{outStatements.map(statement => <li key={statement.id}>
							<button type="button" aria-label={t("accountant.openStatement", { id: statement.id })} onClick={() => S_STATEMENT_POPUP.invoke({ id: statement.id })}>
								<span className="id">{c("notPaid.document", { id: statement.id })}</span>
								<span className="meta">{typeLabel(statement.type)} · {statement.periodFrom && statement.periodTo ? dayRange(statement.periodFrom, statement.periodTo) : "—"} · {c("notPaid.payments", { count: statement.count })}</span>
								<span className="num">{money(statement.amount[currency])}</span>
							</button>
						</li>)}</Entries>
						{renderLateSettledEntries(billedTransactions)}
						{lateSettled?.truncated && <p className="aside">{t("accountant.lateSettledTruncated")}</p>}
					</details> : <div className="row">
						<span className="what plain">{c("notPaid.next")}</span>
						<span className={`amount${muted(deferredOut)}`}>{atLeast(deferredOut)}</span>
					</div>}</li>}
					{unsettled && <li>{transactions.length ? <details>
						<summary className="row">
							<span className="what"><ChevronRight size={16} className="chev" aria-hidden="true" />{c("notPaid.acquirer")}{!!stuckCount && <span className="badge">{c("notPaid.check", { count: stuckCount })}</span>}</span>
							<span className={`amount${muted(unsettledAmount)}`}>{atLeast(unsettledAmount)}</span>
							<span className="why">
								{stuckCount && stuckAfter !== undefined && stuckAmount !== undefined
									? <Trans t={t} i18nKey="accountant.calm.notPaid.stuck" count={stuckAfter} values={{ amount: money(stuckAmount), count: stuckAfter }} components={{ b: <b /> }} />
									: c("notPaid.acquirerHint")}
								{!!lostStatuses.length && <> {c("notPaid.lost")}</>}
								{unsettledCount !== undefined && <> {c("notPaid.payments", { count: unsettledCount })}.</>}
							</span>
						</summary>
						<Entries>
							{transactions.map(transaction => <li key={transaction.id}>
								<button type="button" aria-label={t("accountant.openTransaction", { id: transaction.id })} onClick={() => openTransaction(transaction)}>
									<span className="id">{c("notPaid.payment", { id: transaction.id })}</span>
									<span className="meta">{transactionMeta(transaction)}</span>
									<span className="num">{formatProfitAmount(transaction.amount, transaction.currency, locale)}</span>
								</button>
							</li>)}
							{unsettled.truncated && <li className="more">{c("notPaid.shown", { shown: unsettled.transactions.length, total: unsettled.total })}</li>}
						</Entries>
					</details> : <div className="row">
						<span className="what plain">{c("notPaid.acquirer")}</span>
						<span className="amount muted">{shown(unsettledAmount)}</span>
						<span className="why">{c("notPaid.none")}</span>
					</div>}</li>}
					{showNotBilled && <li style={{ margin: 12, border: `1px solid ${hasNotBilled ? "#fda29b" : "#d0d5dd"}`, borderRadius: 12, background: "#fff", color: hasNotBilled ? "#b42318" : "#344054", boxShadow: "0 1px 2px rgba(16,24,40,.05)" }}>
						<div className="row"><span className="what plain" style={{ color: hasNotBilled ? "#b42318" : "#344054" }}>{t("accountant.notBilled")}</span><span className="amount" style={{ color: hasNotBilled ? "#b42318" : "#667085" }}>{hasNotBilled ? shown(notBilledAmount) : t("accountant.deferredNothing", { amount: money(0) })}</span><span className="why">{t("accountant.notBilledDescription")}</span></div>
						{renderLateSettledEntries(notBilledTransactions)}
						{lateSettled?.truncated && <p className="aside">{t("accountant.lateSettledTruncated")}</p>}
					</li>}
				</Rows>
				{reconciled && notPaidOutTotal !== undefined && <Rows as="div"><div className="total"><span>{c("notPaid.total")}</span><span className="amount">{money(notPaidOutTotal)}</span></div></Rows>}
			</Box>
			{deferred && deferredIn !== undefined && !isZero(deferredIn) && <p className="aside"><Trans t={t} i18nKey="accountant.calm.notPaid.earlier" count={deferred.inCount} values={{ amount: money(deferredIn), count: deferred.inCount }} components={{ b: <b /> }} /></p>}
		</Section>;
	};

	// A row the server did not send for this currency is shown as dashes, not as zeros.
	const metricCells = (row: ClientEarningsMetrics | undefined) => row
		? [row.turnover, row.fees, row.rollingTaken, row.rollingReleased, row.refunds, row.chargebacks, row.net].map((value, index) => <td key={index} className={isZero(value) ? "zero" : undefined}>{money(value)}</td>)
		: Array.from({ length: 7 }, (_, index) => <td key={index} className="zero">—</td>);
	const metricHead = (first: string) => <thead><tr><th>{first}</th><th>{c("channels.turnover")}</th><th>{c("channels.fees")}</th><th>{c("channels.taken")}</th><th>{c("channels.released")}</th><th>{c("channels.refunds")}</th><th>{c("channels.chargebacks")}</th><th>{c("channels.net")}</th></tr></thead>;

	const fold = (key: string, title: string, meta: ReactNode, body: ReactNode) => <Fold key={key}>
		<summary><span className="fold-title"><ChevronRight size={16} className="chev" aria-hidden="true" />{title}</span><span className="fold-meta">{meta}</span></summary>
		<div className="fold-body">{body}</div>
	</Fold>;

	const renderDocuments = () => fold("documents", c("docs.title"),
		pendingAmount !== undefined && !isZero(pendingAmount) ? c("docs.pending", { amount: money(pendingAmount) }) : null,
		statements.length ? <Table><table aria-label={c("docs.title")}>
			<thead><tr><th>{c("docs.number")}</th><th>{c("docs.type")}</th><th>{c("docs.status")}</th><th>{c("docs.period")}</th><th>{c("docs.coverage")}</th><th>{c("docs.amount")}</th></tr></thead>
			<tbody>{statements.map(statement => <tr key={statement.id} data-clickable onClick={() => S_STATEMENT_POPUP.invoke({ id: statement.id })}>
				<td><button type="button" className="link" aria-label={t("accountant.openStatement", { id: statement.id })}>№{statement.id}</button></td>
				<td>{typeLabel(statement.type)}</td>
				<td>{statusLabel(statement.status)}</td>
				<td>{dayRange(statement.periodFrom, statement.periodTo)}</td>
				<td><span className={`chip ${statement.fullyInsidePeriod ? "full" : "partial"}`}>{c(statement.fullyInsidePeriod ? "docs.full" : "docs.partial")}</span></td>
				<td>{money(statement.net[currency])}</td>
			</tr>)}</tbody>
		</table></Table> : <p className="plain">{c("docs.empty")}</p>);

	const renderChannels = () => fold("channels", c("channels.title"), null,
		channels.length ? <Table><table aria-label={c("channels.title")}>
			{metricHead(c("channels.channel"))}
			<tbody>
				{channels.map(channel => <tr key={channel.id}><td>{channel.name}</td>{metricCells(channel.currencies[currency])}</tr>)}
				{channels.length > 1 && channelTotal && <tr className="sum"><td>{c("channels.total")}</td>{metricCells(channelTotal)}</tr>}
			</tbody>
		</table></Table> : <p className="plain">{c("channels.empty")}</p>);

	const renderReconciliation = () => {
		if (!data) return null;
		const notes = [...data.reconciliation.notes, ...(deferred?.note ? [deferred.note] : []), ...(serverTail?.note ? [serverTail.note] : [])];
		const plain = (amount: number) => new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(amount);
		return fold("reconciliation", c("recon.title"), c("recon.hint"), <>
			{shiftShown && <p className="plain">{c("recon.shift")} {c("recon.shiftAmounts", { earlier: shown(reportedEarlier), later: shown(reportedLater) })}</p>}
			{mismatch && <p className="plain">{c("recon.unexplained", { amount: shown(mismatchAmount) })}</p>}
			{/* The server's reconciliation equations, shown only when the server says they hold and every term was sent. */}
			{reconciled && metrics && overview && deferredOut !== undefined && unsettledAmount !== undefined && <div className="formula">
				{plain(overview.amount)} <em>{c("formula.created")}</em>
				{" − "}{plain(deferredOut)} <em>{c("formula.deferred")}</em>
				{" − "}{plain(unsettledAmount)} <em>{c("formula.notSettled")}</em>
				{!!notBilledAmount && <>{" − "}{plain(notBilledAmount)} <em>{t("accountant.notBilled")}</em></>}
				{!!deferredIn && <>{" + "}{plain(deferredIn)} <em>{c("formula.earlier")}</em></>}
				{" = "}<b>{plain(metrics.turnover)} {c("formula.byDocuments")}</b>
			</div>}
			{reconciled && metrics && insideTurnover !== undefined && reportedLater !== undefined && reportedEarlier !== undefined && <div className="formula">
				{plain(metrics.turnover)} <em>{c("formula.byDocuments")}</em>
				{" − "}{plain(reportedLater)} <em>{c("formula.outOfReports")}</em>
				{" + "}{plain(reportedEarlier)} <em>{c("formula.intoReports")}</em>
				{!!deferredOut && <>{" + "}{plain(deferredOut)} <em>{c("formula.deferred")}</em></>}
				{!!deferredIn && <>{" − "}{plain(deferredIn)} <em>{c("formula.earlier")}</em></>}
				{!!deferredBeyond && <>{" − "}{plain(deferredBeyond)} <em>{c("formula.beyond")}</em></>}
				{" = "}<b>{plain(insideTurnover)} {c("formula.byReportDays")}</b>
			</div>}
			{byPeriod && <Table><table aria-label={c("recon.reportDays")}>
				{metricHead(c("recon.reportDays"))}
				<tbody>
					<tr><td>{c("recon.inside")}</td>{metricCells(byPeriod.inside[currency])}</tr>
					<tr><td>{c("recon.outside")} <span style={{ color: "#98a2b3" }}>· {byPeriod.outsideDays.length ? c("recon.outsideDays", { days: byPeriod.outsideDays.map(value => day(value)).join(", ") }) : c("recon.noOutside")}</span></td>{metricCells(byPeriod.outside[currency])}</tr>
				</tbody>
			</table></Table>}
			{!!notes.length && <div><div className="notes-title">{c("recon.notes")}</div><ul className="notes">{notes.map(note => <li key={note}>{note}</li>)}</ul></div>}
		</>);
	};

	return <Card style={{ height: "auto", minWidth: 0, gridColumn: "1 / -1" }} role="region" aria-label={c("title")} aria-busy={state.busy}>
		<Head>
			<div>
				<div className="ctx">{t("accountant.title")}</div>
				<h2>{clientName ? `${c("title")} · ${clientName}` : c("title")}</h2>
				<div className="period">{dayRange(from, to, true)}</div>
			</div>
			<div className="tools">
				{currencies.length > 1 && <Select size="small" value={currency} onChange={setSelectedCurrency} values={currencies} />}
				<ReserveRefreshButton $busy={pdfBusy} title={t(pdfBusy ? "accountant.pdfGenerating" : "accountant.exportEarningsPdf")} aria-label={t(pdfBusy ? "accountant.pdfGenerating" : "accountant.exportEarningsPdf")} aria-busy={pdfBusy} disabled={!data || state.busy || pdfBusy} onClick={() => void downloadPdf()}>{pdfBusy ? <LoaderCircle size={18} aria-hidden="true" /> : <FileDown size={18} aria-hidden="true" />}</ReserveRefreshButton>
				<ReserveRefreshButton $busy={state.busy} title={state.error ? t("accountant.retry") : t("accountant.refresh")} aria-label={state.error ? t("accountant.retry") : t("accountant.refresh")} disabled={state.busy || pdfBusy} onClick={() => setAttempt(value => value + 1)}><RotateCw size={16} aria-hidden="true" /></ReserveRefreshButton>
			</div>
		</Head>
		<AdaptiveTabBar label={c("view")} sx={{ marginTop: "12px" }} items={(["paid", "accrued"] as const).map(kind => ({
			id: kind, label: c(kind), icon: kind === "paid" ? <TabIconCircleCheck size={16} /> : <TabIconClock size={16} />, selected: view === kind, onSelect: () => setView(kind),
		}))} />
		<Body>
			<div role="status" aria-live="polite">
				{state.busy && <span style={{ color: "#667085" }}>{t("accountant.earningsLoading")}</span>}
				{state.error && <span style={{ color: "#b42318" }}>{state.error}</span>}
			</div>
			{pdfError && <div role="alert" style={{ color: "#b42318" }}>{t("accountant.pdfError")}</div>}
			{data && <>
				{renderStatus()}
				{renderTiles()}
				<Section aria-label={c(paid ? "calc.titlePaid" : "calc.titleAccrued")}>
					<h3>{c(paid ? "calc.titlePaid" : "calc.titleAccrued")}</h3>
					{renderCalc()}
				</Section>
				{renderNotPaid()}
				<Section aria-label={c("more")}>
					<h3>{c("more")}</h3>
					{paid && renderDocuments()}
					{renderChannels()}
					{paid && renderReconciliation()}
				</Section>
			</>}
		</Body>
	</Card>;
};
